import React, { useRef, useCallback, useEffect, useState } from 'react';
import { Box, Typography, Popover, IconButton } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useCorkboard, TOOL_MODES } from './CorkboardProvider';
import CorkboardNote from './CorkboardNote';
import useThemeStyles from '../../hooks/useThemeStyles';
import corkboardColors from './corkboardColors.json';

const { lineColors } = corkboardColors;

// Get array of color keys for the picker UI
const LINE_COLOR_KEYS = Object.keys(lineColors);

/**
 * Resolve a color key to its actual hex value based on theme
 * Falls back to treating the value as a raw hex color for backwards compatibility
 */
const resolveLineColor = (colorKey, isDarkMode) => {
  if (!colorKey) return isDarkMode ? '#ffffff' : '#000000';
  const colorEntry = lineColors[colorKey];
  if (colorEntry) {
    return isDarkMode ? colorEntry.dark : colorEntry.light;
  }
  // Fallback: if it's already a hex color, return as-is
  if (colorKey.startsWith('#') || colorKey.startsWith('rgb')) {
    return colorKey;
  }
  return isDarkMode ? '#ffffff' : '#000000';
};

/**
 * CorkboardCanvas - Infinite canvas with pan, zoom, notes, and drawing
 */
const CorkboardCanvas = () => {
  const { colors, isDark } = useThemeStyles();
  const {
    notes,
    drawings,
    viewState,
    setPan,
    setZoom,
    toolMode,
    isDrawing,
    currentDrawing,
    lineStart,
    startDrawing,
    continueDrawing,
    endDrawing,
    cancelLineDrawing,
    addNote,
    isPhantomNoteMode,
    placePhantomNote,
    updatePhantomNotePos,
    cancelPhantomNoteMode,
    selectedDrawingId,
    setSelectedDrawingId,
    deleteDrawing,
    updateDrawingPoints,
    updateDrawingColor,
    draggingEndpoint,
    setDraggingEndpoint,
    draggingDrawing,
    setDraggingDrawing,
    hitTestDrawing,
    hitTestEndpoint,
    findSnapPoint,
  } = useCorkboard();

  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [isPanning, setIsPanning] = useState(false);
  const [hoveredDrawingId, setHoveredDrawingId] = useState(null);
  const [hoveredEndpoint, setHoveredEndpoint] = useState(null);
  const [snapPoint, setSnapPoint] = useState(null);
  const [colorMenuAnchor, setColorMenuAnchor] = useState(null);
  const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  // Use refs for mouse position to avoid re-renders during panning
  const mousePosRef = useRef({ x: 0, y: 0 });
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 }); // Only for phantom note positioning
  const rafPanRef = useRef(null);

  // Local pan state for smooth panning - only syncs to Redux on mouse up
  const [localPan, setLocalPan] = useState(null);
  const { panX: reduxPanX, panY: reduxPanY, zoom } = viewState;
  // Use local pan during drag, otherwise use Redux state
  const panX = localPan?.panX ?? reduxPanX;
  const panY = localPan?.panY ?? reduxPanY;

  // Convert screen coordinates to canvas coordinates
  const screenToCanvas = useCallback(
    (screenX, screenY) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };

      return {
        x: (screenX - rect.left - panX) / zoom,
        y: (screenY - rect.top - panY) / zoom,
      };
    },
    [panX, panY, zoom]
  );

  // Handle wheel for zooming
  const handleWheel = useCallback(
    (e) => {
      e.preventDefault();

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Calculate zoom
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.max(0.1, Math.min(5, zoom * delta));

      // Adjust pan to zoom towards mouse position
      const zoomRatio = newZoom / zoom;
      const newPanX = mouseX - (mouseX - panX) * zoomRatio;
      const newPanY = mouseY - (mouseY - panY) * zoomRatio;

      setPan(newPanX, newPanY);
      setZoom(newZoom);
    },
    [zoom, panX, panY, setPan, setZoom]
  );

  // Handle mouse down
  const handleMouseDown = useCallback(
    (e) => {
      // Allow clicks on container, canvas, or any child elements (for drawing selection)
      // Only skip if clicking on interactive elements like notes
      const { target } = e;
      if (
        target !== containerRef.current &&
        target !== canvasRef.current &&
        !containerRef.current?.contains(target)
      ) {
        return;
      }

      // Skip if clicking on a note or note content (but allow in drawing modes for attachment)
      if (target.closest && (target.closest('[data-corkboard-note]') || target.closest('button'))) {
        // In LINE or PENCIL mode, allow clicks on notes for attachment snapping
        if (toolMode !== TOOL_MODES.LINE && toolMode !== TOOL_MODES.PENCIL) {
          return;
        }
      }

      const canvasPos = screenToCanvas(e.clientX, e.clientY);

      // Handle phantom note placement
      if (isPhantomNoteMode) {
        placePhantomNote(canvasPos.x, canvasPos.y);
        e.preventDefault();
        return;
      }

      if (toolMode === TOOL_MODES.PAN || e.button === 1) {
        // Middle mouse button or pan mode
        setIsPanning(true);
        panStartRef.current = {
          x: e.clientX,
          y: e.clientY,
          panX,
          panY,
        };
        e.preventDefault();
        return;
      }

      // In cursor mode, check for drawing selection or endpoint dragging
      if (toolMode === TOOL_MODES.CURSOR) {
        // First check if clicking on an endpoint
        const endpointHit = hitTestEndpoint(canvasPos, zoom);
        if (endpointHit) {
          setDraggingEndpoint(endpointHit);
          setSelectedDrawingId(endpointHit.drawingId);
          e.preventDefault();
          return;
        }

        // Then check if clicking on a drawing
        const drawingHit = hitTestDrawing(canvasPos, zoom);
        if (drawingHit) {
          setSelectedDrawingId(drawingHit);
          // Start dragging the drawing (if user drags, not just clicks)
          const drawing = drawings.find((d) => d.id === drawingHit);
          if (drawing && drawing.points.length > 0) {
            // Calculate offset from first point to click position
            // Use actual point position if attached
            let firstPoint = drawing.points[0];
            if (drawing.attachments?.[0]) {
              const attachment = drawing.attachments[0];
              const note = notes.find((n) => (n._id || n.id || n.noteId) === attachment.noteId);
              if (note) {
                const { x, y, width, height } = note;
                const { position, type } = attachment;
                if (type === 'corner') {
                  switch (position) {
                    case 'top-left':
                      firstPoint = { x, y };
                      break;
                    case 'top-right':
                      firstPoint = { x: x + width, y };
                      break;
                    case 'bottom-left':
                      firstPoint = { x, y: y + height };
                      break;
                    case 'bottom-right':
                      firstPoint = { x: x + width, y: y + height };
                      break;
                    default:
                      break;
                  }
                } else if (type === 'edge-center') {
                  switch (position) {
                    case 'top':
                      firstPoint = { x: x + width / 2, y };
                      break;
                    case 'bottom':
                      firstPoint = { x: x + width / 2, y: y + height };
                      break;
                    case 'left':
                      firstPoint = { x, y: y + height / 2 };
                      break;
                    case 'right':
                      firstPoint = { x: x + width, y: y + height / 2 };
                      break;
                    default:
                      break;
                  }
                }
              }
            }
            const offset = {
              x: canvasPos.x - firstPoint.x,
              y: canvasPos.y - firstPoint.y,
            };
            setDraggingDrawing({
              drawingId: drawingHit,
              initialPoints: [...drawing.points],
              offset,
            });
          }
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        // Clicked on empty space - deselect
        setSelectedDrawingId(null);
        setDraggingDrawing(null);

        // Double click to create note
        if (e.detail === 2) {
          addNote({
            x: canvasPos.x,
            y: canvasPos.y,
          });
        }
        return;
      }

      // Drawing modes
      if (toolMode === TOOL_MODES.PENCIL || toolMode === TOOL_MODES.LINE) {
        // Deselect when starting new drawing
        setSelectedDrawingId(null);
        // Apply snapping unless shift is held
        const shouldSnap = !e.shiftKey;
        const snap = shouldSnap ? findSnapPoint(canvasPos, zoom) : null;
        const finalPos = snap || canvasPos;
        // Create attachment info if snapped to a note
        const attachment =
          snap && snap.noteId
            ? {
                noteId: snap.noteId,
                type: snap.type,
                position: snap.position,
              }
            : null;
        if (snap) {
          setSnapPoint(snap);
        } else {
          setSnapPoint(null);
        }
        const wasCompletingLine = toolMode === TOOL_MODES.LINE && lineStart !== null;
        startDrawing(finalPos, attachment);
        // Clear snap point after completing line
        if (wasCompletingLine) {
          setSnapPoint(null);
        }
      }
    },
    [
      toolMode,
      panX,
      panY,
      zoom,
      screenToCanvas,
      startDrawing,
      addNote,
      isPhantomNoteMode,
      placePhantomNote,
      hitTestDrawing,
      hitTestEndpoint,
      findSnapPoint,
      lineStart,
      setSelectedDrawingId,
      setDraggingEndpoint,
      setDraggingDrawing,
      drawings,
      notes,
    ]
  );

  // Handle mouse move
  const handleMouseMove = useCallback(
    (e) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        // Always update ref (no re-render)
        mousePosRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      }

      // Handle panning with requestAnimationFrame for smooth performance
      // Uses local state during drag - no Redux dispatches until mouse up
      if (isPanning) {
        const dx = e.clientX - panStartRef.current.x;
        const dy = e.clientY - panStartRef.current.y;
        const newPanX = panStartRef.current.panX + dx;
        const newPanY = panStartRef.current.panY + dy;

        if (rafPanRef.current) {
          cancelAnimationFrame(rafPanRef.current);
        }
        rafPanRef.current = requestAnimationFrame(() => {
          setLocalPan({ panX: newPanX, panY: newPanY });
          rafPanRef.current = null;
        });
        return; // Skip all other processing during pan for performance
      }

      const canvasPos = screenToCanvas(e.clientX, e.clientY);

      // Update hover state for cursor feedback (only in cursor mode when not dragging)
      if (toolMode === TOOL_MODES.CURSOR && !draggingEndpoint && !draggingDrawing) {
        const endpointHit = hitTestEndpoint(canvasPos, zoom);
        const drawingHit = hitTestDrawing(canvasPos, zoom);
        setHoveredEndpoint(endpointHit);
        setHoveredDrawingId(drawingHit);
        setSnapPoint(null); // Clear snap point in cursor mode
      } else if (!draggingDrawing) {
        setHoveredEndpoint(null);
        setHoveredDrawingId(null);
      }

      // Update snap point for line preview (unless shift is held)
      if (toolMode === TOOL_MODES.LINE && lineStart && !e.shiftKey) {
        const snap = findSnapPoint(canvasPos, zoom);
        setSnapPoint(snap);
      } else if (toolMode !== TOOL_MODES.LINE || e.shiftKey) {
        // Clear snap point if not in line mode or shift is held
        if (!draggingEndpoint) {
          setSnapPoint(null);
        }
      }

      // Update phantom note position (in canvas coordinates for placement)
      // Also update state for rendering
      if (isPhantomNoteMode) {
        updatePhantomNotePos(canvasPos.x, canvasPos.y);
        setMousePos(mousePosRef.current);
      }

      if (draggingEndpoint) {
        // Dragging an endpoint - apply snapping unless shift is held
        const shouldSnap = !e.shiftKey;
        const snap = shouldSnap ? findSnapPoint(canvasPos, zoom) : null;
        const finalPos = snap || canvasPos;
        // Create attachment info if snapped to a note
        const attachment =
          snap && snap.noteId
            ? {
                noteId: snap.noteId,
                type: snap.type,
                position: snap.position,
              }
            : null;
        if (snap) {
          setSnapPoint(snap);
        } else {
          setSnapPoint(null);
        }
        const drawing = drawings.find((d) => d.id === draggingEndpoint.drawingId);
        if (drawing && drawing.type === 'line' && drawing.points.length === 2) {
          const newPoints = [...drawing.points];
          newPoints[draggingEndpoint.pointIndex] = finalPos;
          // Update attachments array
          const newAttachments = drawing.attachments ? [...drawing.attachments] : [null, null];
          newAttachments[draggingEndpoint.pointIndex] = attachment;
          updateDrawingPoints(draggingEndpoint.drawingId, newPoints, newAttachments);
        }
      } else if (draggingDrawing) {
        // Dragging the entire drawing
        const { initialPoints, offset } = draggingDrawing;
        // Calculate new position based on current mouse position
        const newFirstPoint = {
          x: canvasPos.x - offset.x,
          y: canvasPos.y - offset.y,
        };
        // Calculate the translation delta
        const deltaX = newFirstPoint.x - initialPoints[0].x;
        const deltaY = newFirstPoint.y - initialPoints[0].y;
        // Apply translation to all points
        const newPoints = initialPoints.map((point) => ({
          x: point.x + deltaX,
          y: point.y + deltaY,
        }));
        updateDrawingPoints(draggingDrawing.drawingId, newPoints);
      } else if (isDrawing && toolMode === TOOL_MODES.PENCIL) {
        continueDrawing(canvasPos);
      }
    },
    [
      isPanning,
      isDrawing,
      toolMode,
      screenToCanvas,
      continueDrawing,
      isPhantomNoteMode,
      updatePhantomNotePos,
      draggingEndpoint,
      draggingDrawing,
      drawings,
      updateDrawingPoints,
      zoom,
      hitTestEndpoint,
      hitTestDrawing,
      findSnapPoint,
      lineStart,
      setSnapPoint,
    ]
  );

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    // Cancel any pending pan animation
    if (rafPanRef.current) {
      cancelAnimationFrame(rafPanRef.current);
      rafPanRef.current = null;
    }
    // Sync local pan to Redux state on mouse up
    if (localPan) {
      setPan(localPan.panX, localPan.panY);
      setLocalPan(null);
    }
    setIsPanning(false);
    if (isDrawing) {
      endDrawing();
    }
    if (draggingEndpoint) {
      setDraggingEndpoint(null);
    }
    if (draggingDrawing) {
      setDraggingDrawing(null);
    }
  }, [
    isDrawing,
    endDrawing,
    draggingEndpoint,
    draggingDrawing,
    setDraggingEndpoint,
    setDraggingDrawing,
    localPan,
    setPan, // eslint-disable-line react-hooks/exhaustive-deps
  ]);

  // Draw canvas - optimized to avoid unnecessary redraws
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    const rect = containerRef.current?.getBoundingClientRect();

    if (!rect) return undefined;

    // Set canvas size only if changed
    if (canvas.width !== rect.width || canvas.height !== rect.height) {
      canvas.width = rect.width;
      canvas.height = rect.height;
    }

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Apply transform
    ctx.save();
    ctx.translate(panX, panY);
    ctx.scale(zoom, zoom);

    // Draw grid
    ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
    ctx.lineWidth = 1 / zoom;
    const gridSize = 50;
    const startX = Math.floor(-panX / zoom / gridSize) * gridSize - gridSize;
    const startY = Math.floor(-panY / zoom / gridSize) * gridSize - gridSize;
    const endX = startX + rect.width / zoom + gridSize * 2;
    const endY = startY + rect.height / zoom + gridSize * 2;

    for (let x = startX; x < endX; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, startY);
      ctx.lineTo(x, endY);
      ctx.stroke();
    }
    for (let y = startY; y < endY; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(startX, y);
      ctx.lineTo(endX, y);
      ctx.stroke();
    }

    // Helper to get point position (handles attachments dynamically)
    // Exported for use in render
    const getPointPosition = (point, attachment) => {
      if (attachment && attachment.noteId) {
        const note = notes.find((n) => (n._id || n.id || n.noteId) === attachment.noteId);
        if (note) {
          const { x, y, width, height } = note;
          const { position, type } = attachment;

          if (type === 'corner') {
            switch (position) {
              case 'top-left':
                return { x, y };
              case 'top-right':
                return { x: x + width, y };
              case 'bottom-left':
                return { x, y: y + height };
              case 'bottom-right':
                return { x: x + width, y: y + height };
              default:
                return point;
            }
          } else if (type === 'edge-center') {
            switch (position) {
              case 'top':
                return { x: x + width / 2, y };
              case 'bottom':
                return { x: x + width / 2, y: y + height };
              case 'left':
                return { x, y: y + height / 2 };
              case 'right':
                return { x: x + width, y: y + height / 2 };
              default:
                return point;
            }
          }
        }
      }
      return point;
    };

    // Draw saved drawings
    drawings.forEach((drawing) => {
      if (drawing.points.length < 2) return;

      const isSelected = drawing.id === selectedDrawingId;
      const lineWidth = isSelected ? 3 / zoom : 2 / zoom;

      // Calculate actual point positions (handling attachments dynamically)
      const actualPoints = drawing.points.map((point, index) => {
        const attachment = drawing.attachments?.[index];
        return getPointPosition(point, attachment, index);
      });

      ctx.strokeStyle = resolveLineColor(drawing.color, isDark);
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      ctx.beginPath();
      ctx.moveTo(actualPoints[0].x, actualPoints[0].y);

      if (drawing.type === 'freehand') {
        actualPoints.forEach((point) => {
          ctx.lineTo(point.x, point.y);
        });
      } else if (drawing.type === 'line') {
        ctx.lineTo(actualPoints[1].x, actualPoints[1].y);
      }

      ctx.stroke();

      // Draw selection highlight
      if (isSelected) {
        ctx.strokeStyle = 'rgba(33, 150, 243, 0.5)';
        ctx.lineWidth = 4 / zoom;
        ctx.setLineDash([5 / zoom, 5 / zoom]);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Draw endpoint handles for selected lines
      if (isSelected && drawing.type === 'line' && drawing.points.length === 2) {
        const handleSize = 8 / zoom;
        actualPoints.forEach((point) => {
          ctx.fillStyle = 'rgba(33, 150, 243, 0.8)';
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2 / zoom;
          ctx.beginPath();
          ctx.arc(point.x, point.y, handleSize, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        });
      }
    });

    // Draw current drawing in progress
    if (currentDrawing && currentDrawing.points.length > 0) {
      ctx.strokeStyle = resolveLineColor(currentDrawing.color, isDark);
      ctx.lineWidth = 2 / zoom;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      ctx.beginPath();
      ctx.moveTo(currentDrawing.points[0].x, currentDrawing.points[0].y);
      currentDrawing.points.forEach((point) => {
        ctx.lineTo(point.x, point.y);
      });
      ctx.stroke();
    }

    // Draw line preview - use ref for smooth preview without state updates
    if (lineStart && toolMode === TOOL_MODES.LINE) {
      const currentRect = containerRef.current?.getBoundingClientRect();
      const currentCanvasPos = currentRect
        ? screenToCanvas(
            mousePosRef.current.x + currentRect.left,
            mousePosRef.current.y + currentRect.top
          )
        : { x: 0, y: 0 };

      // Use snap point if available, otherwise use current position
      const previewEnd = snapPoint || currentCanvasPos;

      ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)';
      ctx.lineWidth = 2 / zoom;
      ctx.setLineDash([5 / zoom, 5 / zoom]);

      ctx.beginPath();
      ctx.moveTo(lineStart.x, lineStart.y);
      ctx.lineTo(previewEnd.x, previewEnd.y);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Draw snap point indicator
    if (snapPoint) {
      const isAttachment = snapPoint.noteId !== undefined;
      const snapSize = isAttachment ? 8 / zoom : 6 / zoom;

      // Different colors for attachments (note) vs regular snaps (line endpoints)
      if (isAttachment) {
        // Attachment to note - green with pulsing effect
        ctx.fillStyle = 'rgba(76, 175, 80, 0.95)';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 3 / zoom;
        // Draw outer ring for attachment indicator
        ctx.beginPath();
        ctx.arc(snapPoint.x, snapPoint.y, snapSize + 2 / zoom, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(76, 175, 80, 0.4)';
        ctx.lineWidth = 2 / zoom;
        ctx.stroke();
      } else {
        // Regular snap to line endpoint - blue
        ctx.fillStyle = 'rgba(33, 150, 243, 0.9)';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2 / zoom;
      }

      ctx.beginPath();
      ctx.arc(snapPoint.x, snapPoint.y, snapSize, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2 / zoom;
      ctx.stroke();

      // Draw attachment icon (link symbol) for note attachments
      if (isAttachment) {
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2 / zoom;
        ctx.beginPath();
        // Draw a small link/chain icon
        const iconSize = 4 / zoom;
        ctx.moveTo(snapPoint.x - iconSize, snapPoint.y);
        ctx.lineTo(snapPoint.x + iconSize, snapPoint.y);
        ctx.moveTo(snapPoint.x - iconSize / 2, snapPoint.y - iconSize / 2);
        ctx.arc(snapPoint.x - iconSize / 2, snapPoint.y, iconSize / 2, -Math.PI / 2, Math.PI / 2);
        ctx.moveTo(snapPoint.x + iconSize / 2, snapPoint.y - iconSize / 2);
        ctx.arc(snapPoint.x + iconSize / 2, snapPoint.y, iconSize / 2, -Math.PI / 2, Math.PI / 2);
        ctx.stroke();
      }
    }

    ctx.restore();

    // Return cleanup for animation frame loop if in line mode
    return undefined;
  }, [
    panX,
    panY,
    zoom,
    drawings,
    notes,
    currentDrawing,
    lineStart,
    toolMode,
    screenToCanvas,
    selectedDrawingId,
    snapPoint,
    isDark,
  ]);

  // Animation loop for line preview - runs continuously while in line mode with lineStart
  useEffect(() => {
    if (!lineStart || toolMode !== TOOL_MODES.LINE) return undefined;

    let animationId;
    let lastMouseX = 0;
    let lastMouseY = 0;

    // Helper to get point position (handles attachments dynamically)
    const getPointPosition = (point, attachment) => {
      if (attachment && attachment.noteId) {
        const note = notes.find((n) => (n._id || n.id || n.noteId) === attachment.noteId);
        if (note) {
          const { x, y, width, height } = note;
          const { position, type } = attachment;

          if (type === 'corner') {
            switch (position) {
              case 'top-left':
                return { x, y };
              case 'top-right':
                return { x: x + width, y };
              case 'bottom-left':
                return { x, y: y + height };
              case 'bottom-right':
                return { x: x + width, y: y + height };
              default:
                return point;
            }
          } else if (type === 'edge-center') {
            switch (position) {
              case 'top':
                return { x: x + width / 2, y };
              case 'bottom':
                return { x: x + width / 2, y: y + height };
              case 'left':
                return { x, y: y + height / 2 };
              case 'right':
                return { x: x + width, y: y + height / 2 };
              default:
                return point;
            }
          }
        }
      }
      return point;
    };

    const animate = () => {
      // Only request redraw if mouse position changed
      if (mousePosRef.current.x !== lastMouseX || mousePosRef.current.y !== lastMouseY) {
        lastMouseX = mousePosRef.current.x;
        lastMouseY = mousePosRef.current.y;

        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext('2d');
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect && ctx) {
            // Redraw everything
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.save();
            ctx.translate(panX, panY);
            ctx.scale(zoom, zoom);

            // Draw grid
            ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
            ctx.lineWidth = 1 / zoom;
            const gridSize = 50;
            const startX = Math.floor(-panX / zoom / gridSize) * gridSize - gridSize;
            const startY = Math.floor(-panY / zoom / gridSize) * gridSize - gridSize;
            const endX = startX + rect.width / zoom + gridSize * 2;
            const endY = startY + rect.height / zoom + gridSize * 2;

            for (let x = startX; x < endX; x += gridSize) {
              ctx.beginPath();
              ctx.moveTo(x, startY);
              ctx.lineTo(x, endY);
              ctx.stroke();
            }
            for (let y = startY; y < endY; y += gridSize) {
              ctx.beginPath();
              ctx.moveTo(startX, y);
              ctx.lineTo(endX, y);
              ctx.stroke();
            }

            // Draw existing drawings
            drawings.forEach((drawing) => {
              if (drawing.points.length < 2) return;

              const isSelected = drawing.id === selectedDrawingId;
              const lineWidth = isSelected ? 3 / zoom : 2 / zoom;

              // Calculate actual point positions (handling attachments dynamically)
              const actualPoints = drawing.points.map((point, index) => {
                const attachment = drawing.attachments?.[index];
                return getPointPosition(point, attachment);
              });

              ctx.strokeStyle = resolveLineColor(drawing.color, isDark);
              ctx.lineWidth = lineWidth;
              ctx.lineCap = 'round';
              ctx.lineJoin = 'round';

              ctx.beginPath();
              ctx.moveTo(actualPoints[0].x, actualPoints[0].y);

              if (drawing.type === 'freehand') {
                actualPoints.forEach((point) => {
                  ctx.lineTo(point.x, point.y);
                });
              } else if (drawing.type === 'line') {
                ctx.lineTo(actualPoints[1].x, actualPoints[1].y);
              }

              ctx.stroke();
            });

            // Draw line preview
            const currentCanvasPos = {
              x: (lastMouseX - panX) / zoom,
              y: (lastMouseY - panY) / zoom,
            };
            const previewEnd = snapPoint || currentCanvasPos;

            ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)';
            ctx.lineWidth = 2 / zoom;
            ctx.setLineDash([5 / zoom, 5 / zoom]);
            ctx.beginPath();
            ctx.moveTo(lineStart.x, lineStart.y);
            ctx.lineTo(previewEnd.x, previewEnd.y);
            ctx.stroke();
            ctx.setLineDash([]);

            // Draw snap point indicator
            if (snapPoint) {
              const isAttachment = snapPoint.noteId !== undefined;
              const snapSize = isAttachment ? 8 / zoom : 6 / zoom;

              if (isAttachment) {
                ctx.fillStyle = 'rgba(76, 175, 80, 0.95)';
              } else {
                ctx.fillStyle = 'rgba(33, 150, 243, 0.9)';
              }

              ctx.beginPath();
              ctx.arc(snapPoint.x, snapPoint.y, snapSize, 0, Math.PI * 2);
              ctx.fill();
              ctx.strokeStyle = '#ffffff';
              ctx.lineWidth = 2 / zoom;
              ctx.stroke();
            }

            ctx.restore();
          }
        }
      }
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [
    lineStart,
    toolMode,
    panX,
    panY,
    zoom,
    snapPoint,
    drawings,
    notes,
    selectedDrawingId,
    isDark,
  ]);

  // Attach wheel event listener (passive: false for preventDefault)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  // Cursor style based on tool mode
  const getCursor = () => {
    if (isPanning) return 'grabbing';
    if (isPhantomNoteMode) return 'crosshair';
    if (draggingEndpoint) return 'move';
    if (draggingDrawing) return 'move';
    if (toolMode === TOOL_MODES.CURSOR) {
      if (hoveredEndpoint) return 'move';
      if (hoveredDrawingId) return 'move';
    }
    switch (toolMode) {
      case TOOL_MODES.PAN:
        return 'grab';
      case TOOL_MODES.PENCIL:
      case TOOL_MODES.LINE:
        return 'crosshair';
      default:
        return 'default';
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Delete selected drawing
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedDrawingId) {
        e.preventDefault();
        deleteDrawing(selectedDrawingId);
        setSelectedDrawingId(null);
      }

      // Escape to cancel various modes
      if (e.key === 'Escape') {
        if (lineStart) {
          // Cancel line drawing in progress
          cancelLineDrawing();
        } else if (isPhantomNoteMode) {
          cancelPhantomNoteMode();
        } else if (selectedDrawingId) {
          setSelectedDrawingId(null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    selectedDrawingId,
    deleteDrawing,
    setSelectedDrawingId,
    isPhantomNoteMode,
    cancelPhantomNoteMode,
    lineStart,
    cancelLineDrawing,
  ]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: colors.background.primary,
        cursor: getCursor(),
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Drawing canvas layer */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      />

      {/* Notes layer */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          transformOrigin: '0 0',
          transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
          willChange: 'transform', // Hint to browser for GPU acceleration
        }}
      >
        {notes.map((note) => (
          <CorkboardNote key={note._id || note.id || note.noteId} note={note} />
        ))}
      </Box>

      {/* Phantom note preview - rendered in screen space to follow cursor smoothly */}
      {isPhantomNoteMode && (
        <Box
          sx={{
            position: 'absolute',
            left: mousePos.x - 100, // Center the note on cursor (half of 200px width)
            top: mousePos.y - 75, // Center the note on cursor (half of 150px height)
            width: 200,
            minHeight: 150,
            backgroundColor: isDark ? 'rgba(66, 66, 66, 0.6)' : 'rgba(254, 243, 199, 0.6)',
            border: `2px dashed ${isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'}`,
            borderRadius: 1,
            pointerEvents: 'none',
            zIndex: 999,
            display: 'flex',
            flexDirection: 'column',
            opacity: 0.8,
          }}
        >
          <Box
            sx={{
              p: 0.5,
              backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                fontStyle: 'italic',
                fontSize: '0.7rem',
              }}
            >
              New Note
            </Typography>
          </Box>
          <Box sx={{ p: 1.5, flexGrow: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 'bold',
                mb: 0.5,
                color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
              }}
            >
              Untitled
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                fontSize: '0.8rem',
              }}
            >
              Click to place note
            </Typography>
          </Box>
        </Box>
      )}

      {/* Zoom indicator */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 80,
          right: 16,
          backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.6)',
          color: isDark ? colors.text.primary : 'white',
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          fontSize: '0.75rem',
          fontWeight: 'bold',
        }}
      >
        {Math.round(zoom * 100)}%
      </Box>

      {/* Toolbar for selected drawing (line or freehand) */}
      {selectedDrawingId &&
        (() => {
          const selectedDrawing = drawings.find((d) => d.id === selectedDrawingId);
          if (!selectedDrawing || !selectedDrawing.points || selectedDrawing.points.length < 2) {
            return null;
          }

          // Helper to get point position (handles attachments)
          const getPointPos = (point, attachment) => {
            if (attachment && attachment.noteId) {
              const note = notes.find((n) => (n._id || n.id || n.noteId) === attachment.noteId);
              if (note) {
                const { x, y, width, height } = note;
                const { position, type } = attachment;

                if (type === 'corner') {
                  switch (position) {
                    case 'top-left':
                      return { x, y };
                    case 'top-right':
                      return { x: x + width, y };
                    case 'bottom-left':
                      return { x, y: y + height };
                    case 'bottom-right':
                      return { x: x + width, y: y + height };
                    default:
                      return point;
                  }
                } else if (type === 'edge-center') {
                  switch (position) {
                    case 'top':
                      return { x: x + width / 2, y };
                    case 'bottom':
                      return { x: x + width / 2, y: y + height };
                    case 'left':
                      return { x, y: y + height / 2 };
                    case 'right':
                      return { x: x + width, y: y + height / 2 };
                    default:
                      return point;
                  }
                }
              }
            }
            return point;
          };

          // Calculate center position of the drawing
          const actualPoints = selectedDrawing.points.map((point, index) => {
            const attachment = selectedDrawing.attachments?.[index];
            return getPointPos(point, attachment);
          });

          // Find center (works for both lines and freehand)
          let centerX;
          let centerY;
          if (selectedDrawing.type === 'line' && actualPoints.length >= 2) {
            centerX = (actualPoints[0].x + actualPoints[1].x) / 2;
            centerY = (actualPoints[0].y + actualPoints[1].y) / 2;
          } else {
            // For freehand, find bounding box center
            const xs = actualPoints.map((p) => p.x);
            const ys = actualPoints.map((p) => p.y);
            centerX = (Math.min(...xs) + Math.max(...xs)) / 2;
            centerY = (Math.min(...ys) + Math.max(...ys)) / 2;
          }

          // Convert canvas coordinates to screen coordinates
          const screenX = centerX * zoom + panX;
          const screenY = centerY * zoom + panY;

          return (
            <>
              {/* Pointer line from toolbar to drawing center */}
              <Box
                sx={{
                  position: 'absolute',
                  left: screenX,
                  top: screenY - 20,
                  width: 2,
                  height: 20,
                  backgroundColor: 'rgba(33, 150, 243, 0.6)',
                  zIndex: 999,
                  pointerEvents: 'none',
                }}
              />
              {/* Arrow pointer at the end of the line */}
              <Box
                sx={{
                  position: 'absolute',
                  left: screenX - 6,
                  top: screenY - 2,
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: '6px solid rgba(33, 150, 243, 0.8)',
                  zIndex: 999,
                  pointerEvents: 'none',
                }}
              />
              {/* Toolbar container */}
              <Box
                sx={{
                  position: 'absolute',
                  left: screenX - 44,
                  top: screenY - 44,
                  display: 'flex',
                  gap: 0.5,
                  backgroundColor: isDark ? 'rgba(50, 50, 50, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                  borderRadius: 2,
                  padding: 0.5,
                  boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.2)',
                  zIndex: 1000,
                }}
              >
                {/* Color button */}
                <Box
                  onClick={(e) => setColorMenuAnchor(e.currentTarget)}
                  sx={{
                    width: 36,
                    height: 36,
                    backgroundColor: 'transparent',
                    border: '2px solid rgba(33, 150, 243, 0.8)',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      backgroundColor: 'rgba(33, 150, 243, 0.1)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: resolveLineColor(selectedDrawing.color, isDark),
                      border: `2px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
                    }}
                  />
                </Box>
                {/* Delete button */}
                <IconButton
                  size="small"
                  onClick={() => {
                    deleteDrawing(selectedDrawingId);
                    setSelectedDrawingId(null);
                  }}
                  sx={{
                    width: 36,
                    height: 36,
                    color: 'rgba(244, 67, 54, 0.8)',
                    border: '2px solid rgba(244, 67, 54, 0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      color: 'rgba(244, 67, 54, 1)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Color picker popover */}
              <Popover
                open={Boolean(colorMenuAnchor)}
                anchorEl={colorMenuAnchor}
                onClose={() => setColorMenuAnchor(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
              >
                <Box sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', width: 200, gap: 0.5 }}>
                  {LINE_COLOR_KEYS.map((colorKey) => {
                    const displayColor = resolveLineColor(colorKey, isDark);
                    const isSelected = selectedDrawing.color === colorKey;
                    return (
                      <Box
                        key={colorKey}
                        onClick={() => {
                          if (updateDrawingColor && selectedDrawingId) {
                            updateDrawingColor(selectedDrawingId, colorKey);
                          }
                          setColorMenuAnchor(null);
                        }}
                        title={colorKey}
                        sx={{
                          width: 28,
                          height: 28,
                          backgroundColor: displayColor,
                          borderRadius: 0.5,
                          cursor: 'pointer',
                          border: isSelected
                            ? '2px solid #1976d2'
                            : `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
                          '&:hover': {
                            transform: 'scale(1.1)',
                          },
                          transition: 'transform 0.2s',
                        }}
                      />
                    );
                  })}
                </Box>
              </Popover>
            </>
          );
        })()}
    </Box>
  );
};

export default CorkboardCanvas;
